import React, { memo, useState, useEffect, useRef } from 'react';
import { Handle, Position } from 'reactflow';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';

const NodeCard = ({ data, isConnectable, selected }) => {
  const [showTooltip, setShowTooltip] = useState(false);
  const tooltipRef = useRef(null);

  const { 
    content, 
    image_url, 
    is_start_node, 
    is_end_node, 
    onEdit, 
    onDelete,
    onSetStart, 
    onSetEnd, 
    onUnsetStart, 
    onUnsetEnd, 
    onDoubleClick,
    options = []
  } = data;

  // Updated node style to use red for end nodes
  const nodeStyle = is_start_node 
    ? 'bg-green-50 border-2 border-green-400' 
    : is_end_node 
      ? 'bg-red-50 border-2 border-red-400' 
      : 'bg-white border border-purple-200';

  // Handle clicking outside tooltip to close it
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (tooltipRef.current && !tooltipRef.current.contains(event.target)) {
        setShowTooltip(false);
      }
    };

    if (showTooltip) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showTooltip]);

  return (
    <div 
      className={`p-4 rounded-xl shadow-soft ${nodeStyle} ${selected ? 'ring-2 ring-primary-500' : ''} relative min-w-[180px]`}
      onDoubleClick={(e) => {
        e.stopPropagation();
        if (onDoubleClick) onDoubleClick();
      }}
    >
      {/* Handles */}
      <Handle
        type="target"
        position={Position.Top}
        isConnectable={isConnectable}
        className="w-3 h-3 bg-purple-500"
      />
      <Handle
        type="source"
        position={Position.Bottom}
        isConnectable={isConnectable}
        className="w-3 h-3 bg-purple-500"
      />

      {/* Quick view button */}
      <motion.button
        whileHover={{ scale: 1.1 }}
        whileTap={{ scale: 0.9 }}
        onClick={(e) => {
          e.stopPropagation();
          setShowTooltip(!showTooltip);
        }}
        className="absolute top-2 right-2 p-1 bg-purple-100 hover:bg-purple-200 text-purple-700 rounded-full z-10"
        title="Quick view"
      >
        <SafeIcon icon={showTooltip ? FiIcons.FiEyeOff : FiIcons.FiEye} className="text-xs" />
      </motion.button>

      {/* Tooltip with content and options */}
      <AnimatePresence>
        {showTooltip && (
          <motion.div
            ref={tooltipRef}
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="absolute z-50 bg-white p-4 rounded-xl shadow-dreamy border border-purple-200 max-w-md"
            style={{
              bottom: '100%',
              left: '50%',
              transform: 'translateX(-50%)',
              marginBottom: '10px',
              width: '320px'
            }}
          >
            <div className="flex justify-between items-center mb-2">
              <strong className="text-purple-700">Node Content</strong>
              <button 
                className="text-gray-400 hover:text-gray-600" 
                onClick={() => setShowTooltip(false)}
              >
                <SafeIcon icon={FiIcons.FiX} className="text-sm" />
              </button>
            </div>
            
            <div className="text-sm text-gray-700 mb-3">
              <div className="mt-1">{content || 'No content'}</div>
            </div>
            
            {options.length > 0 && (
              <div className="text-sm text-gray-700">
                <strong className="text-purple-700">Options:</strong>
                <ul className="mt-1 space-y-1">
                  {options.map((option, index) => (
                    <li key={option.id} className="flex items-start gap-2">
                      <span className="text-purple-600 font-medium">{index + 1}.</span>
                      <span className="flex-1">
                        {option.option_text}
                        {!option.next_node_id && (
                          <span className="text-red-500 text-xs ml-2">(no destination)</span>
                        )}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
            
            {options.length === 0 && (
              <div className="text-sm text-gray-500 italic">
                No options added yet
              </div>
            )}
            
            <div
              className="absolute bottom-0 left-1/2 transform -translate-x-1/2 translate-y-1/2 rotate-45 w-3 h-3 bg-white border-r border-b border-purple-200"
            ></div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Node content */}
      <div className="mb-2">
        {/* Node type indicator */}
        {is_start_node && (
          <div className="flex items-center gap-1 mb-2">
            <SafeIcon icon={FiIcons.FiFlag} className="text-xs text-green-600" />
            <span className="text-xs font-bold text-green-600">START</span>
          </div>
        )}
        {is_end_node && (
          <div className="flex items-center gap-1 mb-2">
            <SafeIcon icon={FiIcons.FiXCircle} className="text-xs text-red-600" />
            <span className="text-xs font-bold text-red-600">END</span>
          </div>
        )}

        {/* Node content preview */}
        <p className="text-sm font-fun text-gray-800 line-clamp-3">
          {content ? content.substring(0, 50) + (content.length > 50 ? '...' : '') : 'Empty node'}
        </p>
      </div>

      {/* Image indicator */}
      {image_url && (
        <div className="flex items-center gap-1 mb-2">
          <SafeIcon icon={FiIcons.FiImage} className="text-xs text-purple-500" />
          <span className="text-xs text-purple-500">Has image</span>
        </div>
      )}

      {/* Action buttons */}
      <div className="flex justify-between items-center">
        <div className="flex gap-1">
          {onEdit && (
            <motion.button
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.9 }}
              onClick={(e) => {
                e.stopPropagation();
                onEdit();
              }}
              className="p-1 bg-blue-100 hover:bg-blue-200 text-blue-700 rounded"
              title="Edit node"
            >
              <SafeIcon icon={FiIcons.FiEdit2} className="text-xs" />
            </motion.button>
          )}
          {onDelete && (
            <motion.button
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.9 }}
              onClick={(e) => {
                e.stopPropagation();
                onDelete();
              }}
              className="p-1 bg-red-100 hover:bg-red-200 text-red-700 rounded"
              title="Delete node"
            >
              <SafeIcon icon={FiIcons.FiTrash2} className="text-xs" />
            </motion.button>
          )}
        </div>
        <div className="flex gap-1">
          {!is_start_node && onSetStart && (
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={(e) => {
                e.stopPropagation();
                onSetStart();
              }}
              className="text-xs px-2 py-1 bg-green-100 hover:bg-green-200 text-green-700 rounded"
              title="Set as start"
            >
              Start
            </motion.button>
          )}
          {is_start_node && onUnsetStart && (
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={(e) => {
                e.stopPropagation();
                onUnsetStart();
              }}
              className="text-xs px-2 py-1 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded"
              title="Unset start"
            >
              Unset
            </motion.button>
          )}
          {!is_end_node && onSetEnd && (
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={(e) => {
                e.stopPropagation();
                onSetEnd();
              }}
              className="text-xs px-2 py-1 bg-red-100 hover:bg-red-200 text-red-700 rounded"
              title="Set as end"
            >
              End
            </motion.button>
          )}
          {is_end_node && onUnsetEnd && (
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={(e) => {
                e.stopPropagation();
                onUnsetEnd();
              }}
              className="text-xs px-2 py-1 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded"
              title="Unset end"
            >
              Unset
            </motion.button>
          )}
        </div>
      </div>
    </div>
  );
};

export default memo(NodeCard);